import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';

import { Button, Modal, SpaceBetween, Toggle } from '@amzn/awsui-components-react-v3';

import { UsageAgreementDiff } from 'src/components/datausageagreement/usageAgreementDiff';
import { getDataAgreement } from 'src/api/d3a';
import { FORM_CONTEXTS_FOR_REVIEWS } from 'src/components/datausageagreement/utils';
import { renderValidationList } from 'src/components/reviews/utils';

export interface ReviewAgreementDataDiffTableProps {
  diff: any;
  visible: boolean;
  dismiss: any;
  agreementValidationResult?: string;
}

export const ReviewAgreementDataDiffModal = (props: ReviewAgreementDataDiffTableProps) => {
  const [agreementDiffPropsList, setAgreementDiffPropsList] = useState([]);
  const [showChangesOnly, setShowChangesOnly] = useState(false);
  const [error, setError] = useState([]);
  const [loadingModal, setLoadingModal] = useState(false);

  useEffect(() => {
    handleRefresh();
  }, [props.diff]);

  const handleRefresh = async () => {
    setLoadingModal(true);
    let agreementDiffList = [];
    if (props.diff != null) {
      for (let agreementDiff of props.diff) {
        const getDataAgreementOutput = await getDataAgreement({ id: agreementDiff.agreementId });
        const schema = JSON.stringify(getDataAgreementOutput.dataAgreements[0]?.jsonSchema ?? '{}');
        const afterFormData = JSON.parse(agreementDiff.afterFormData[0].agreementData);
        const beforeFormData = JSON.parse(agreementDiff.beforeFormData[0].agreementData);
        agreementDiffList.push({
          agreementId: agreementDiff.agreementId,
          afterFormData: afterFormData,
          beforeFormData: beforeFormData,
          schema: schema,
          readOnly: true,
          errors: error,
          setErrors: setError,
        });
      }
      setAgreementDiffPropsList(agreementDiffList);
    }
    setLoadingModal(false);
  };

  const parsedValidationResults = useMemo(() => {
    try {
      return props.agreementValidationResult ? JSON.parse(props.agreementValidationResult) : [];
    } catch {
      return [];
    }
  }, [props.agreementValidationResult]);

  const getValidationListForAgreement = (agreementId: string) => {
    const match = parsedValidationResults.find((r) => r.agreementId === agreementId);
    return match?.validationList ?? [];
  };

  const renderAgreementDiffForms = (diffList) => {
    let res = [];
    for (let diff of diffList) {
      res.push(
        <SpaceBetween size={'s'} direction={'vertical'} key={diff.agreementId}>
          <UsageAgreementDiff
            beforeFormData={diff.beforeFormData}
            afterFormData={diff.afterFormData}
            schema={diff.schema}
            errors={error}
            setErrors={setError}
            showChangesOnly={showChangesOnly}
            contexts={FORM_CONTEXTS_FOR_REVIEWS}
          />
          {renderValidationList(getValidationListForAgreement(diff.agreementId))}
        </SpaceBetween>,
      );
    }
    return res;
  };

  return (
    <>
      <Modal
        onDismiss={props.dismiss}
        visible={props.visible}
        closeAriaLabel='Close modal'
        size='medium'
        header={'Submitted data compared to last review'}
      >
        {loadingModal ? (
          <Button iconName='settings' loading variant='icon' wrapText={false} />
        ) : (
          <>
            <Toggle onChange={({ detail }) => setShowChangesOnly(detail.checked)} checked={showChangesOnly}>
              Show changes only
            </Toggle>
            <br />
            {renderAgreementDiffForms(agreementDiffPropsList)}
          </>
        )}
      </Modal>
    </>
  );
};
